import { registerSW } from 'virtual:pwa-register'
import { StrictMode } from 'react'

registerSW({ immediate: true })
import * as Sentry from '@sentry/react'
import { ClerkProvider } from '@clerk/clerk-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import '@fontsource-variable/manrope'
import '@fontsource-variable/dm-sans'
import '@fontsource-variable/jetbrains-mono'
import './index.css'
import App from './App'
import './i18n'
import { ThemeProvider } from './context/ThemeProvider'
import GlobalFallback from './components/ErrorBoundary/GlobalFallback'
import { logErrorToService, isChunkLoadError } from './utils/errorLogger'
import { initAnalytics } from './lib/analytics'
import { queryClient } from './lib/queryClient'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_ENV,
    sendDefaultPii: true,
    enableLogs: true,
  })
}

initAnalytics()

if (!clerkPublishableKey) {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is required to initialize authentication')
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary
          FallbackComponent={GlobalFallback}
          onError={(error) => {
            if (isChunkLoadError(error)) {
              window.location.reload()
              return
            }
            logErrorToService(error, { context: 'root', severity: 'high' })
          }}
          onReset={() => window.location.reload()}
        >
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </ErrorBoundary>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
)
